import { useAtom, useAtomValue, useSetAtom } from "jotai";

import { useArtworkForm } from "@design-lab/entities/artwork";
import {
  currentPinningSessionAtom,
  designProjectPlacementIdsAtom,
  getPinningSessionForPlacement,
  pinningModeAtom,
  pinningSessionsAtom,
  type DesignLabPlacementKey,
  useCancelPinningSession
} from "@design-lab/entities/pinning";
import {
  trackDesignLabPinningInstructionsRemoved,
  trackDesignLabPinningModeEntered
} from "@design-lab/shared/lib/analytics";
import { infoPath } from "@design-lab/shared/lib/formPathHelpers";
import {
  useDesignLabPinSessionRecording,
  usePinBasedRedrawEnabled,
  usePinSessionForceMixpanelRecording
} from "@design-lab/shared/lib/config";
import { startPinSessionRecording } from "@design-lab/shared/lib/pinSessionRecording";
import { Button } from "@design-lab/shared/ui";

type ActiveSessionIndicatorProps = {
  placementKey: DesignLabPlacementKey;
  placementSide: "front" | "back";
};

export function ActiveSessionIndicator({
  placementKey,
  placementSide
}: ActiveSessionIndicatorProps) {
  const { form } = useArtworkForm();
  const setPinningMode = useSetAtom(pinningModeAtom);
  const [pinningSessions, setPinningSessions] = useAtom(pinningSessionsAtom);
  const designProjectPlacementIds = useAtomValue(designProjectPlacementIdsAtom);
  const setCurrentPinningSession = useSetAtom(currentPinningSessionAtom);
  const { mutate: cancelSession, isPending: isCancellingSession } = useCancelPinningSession();
  const pinSessionRecordingEnabled = useDesignLabPinSessionRecording();
  const forceMixpanelRecording = usePinSessionForceMixpanelRecording();
  const currentArtworkId = form.watch(`${placementKey}.artworkId`) as string | undefined;
  const { designProjectPlacementId, session: sessionForPlacement } = getPinningSessionForPlacement({
    placementKey,
    designProjectPlacementIds,
    pinningSessions,
    artworkId: currentArtworkId
  });

  const hasActiveSession =
    sessionForPlacement?.state === "active" ||
    sessionForPlacement?.state === "generating_preview" ||
    sessionForPlacement?.state === "preview_ready";
  const sessionLookupKey = designProjectPlacementId || currentArtworkId;

  const pinBasedRedrawEnabled = usePinBasedRedrawEnabled();

  if (
    !hasActiveSession ||
    !currentArtworkId ||
    !sessionForPlacement ||
    !sessionLookupKey ||
    !pinBasedRedrawEnabled
  ) {
    return null;
  }

  return (
    <div
      data-testid="active-session-indicator"
      className="mb-4 flex flex-col gap-3 rounded-lg border-2 border-blue-400 bg-blue-100 p-4 sm:flex-row sm:items-center sm:justify-between sm:gap-4"
    >
      <span className="text-sm font-medium text-gray-900 sm:text-base">
        You have unfinished artist edits
      </span>
      <div className="flex gap-2 self-end sm:self-auto">
        <Button
          type="button"
          data-testid="delete-active-session-button"
          className="shrink-0 cursor-pointer rounded-lg border-2 border-red-400 bg-transparent px-4 py-2 text-sm font-medium text-red-600 hover:bg-red-50"
          isDisabled={isCancellingSession}
          onPress={() => {
            const { sessionId } = sessionForPlacement;
            cancelSession(
              { sessionId, artworkId: currentArtworkId },
              {
                onSuccess: () => {
                  trackDesignLabPinningInstructionsRemoved({
                    artworkId: currentArtworkId,
                    sessionId
                  });
                  setPinningSessions((prev: typeof pinningSessions) => {
                    const next = { ...prev };
                    delete next[sessionLookupKey];
                    return next;
                  });
                  form.setValue(infoPath(placementSide, "enhancementRequestNote"), "");
                }
              }
            );
          }}
        >
          Delete
        </Button>
        <Button
          type="button"
          data-testid="continue-session-button"
          className="shrink-0 cursor-pointer rounded-lg bg-blue-700 px-4 py-2 text-sm font-medium text-white hover:bg-blue-800"
          onPress={() => {
            trackDesignLabPinningModeEntered({
              artworkId: currentArtworkId,
              sessionId: sessionForPlacement.sessionId,
              hasExistingPins: true
            });
            setCurrentPinningSession({
              designProjectPlacementId: sessionLookupKey,
              artworkId: currentArtworkId,
              sessionId: sessionForPlacement.sessionId
            });
            startPinSessionRecording({
              enabled: pinSessionRecordingEnabled,
              forceMixpanelRecording
            });
            setPinningMode(true);
          }}
        >
          Continue
        </Button>
      </div>
    </div>
  );
}
